/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Avatar, CircularProgress, Grid, Tooltip } from "@mui/material";
import { Apple, Language, Android } from "@mui/icons-material";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../firebase/firebasev2";

/**
 * Componente personalizado que muestra un Tooltip con el icono correspondiente al tipo de origen.
 * @param {Object} props
 * @param {'administrador' | 'web' | 'android' | 'ios'} props.type El tipo de origen para mostrar el icono correspondiente.
 * @returns {JSX.Element} Un componente Tooltip con el icono correspondiente al tipo de origen.
 */
const CustomToolTip = ({ type }) => {
  return (
    <>
      {type === "administrador" && (
        <Tooltip
          arrow
          title={"Movicare administrador" || "Verificar información"}
        >
          <Avatar
            sx={{ width: 35, height: 35 }}
            src="https://movicaremx.com/IMG/BACKGROUNDS/MoviCare_Circulo.png"
            alt="Movicare administrador"
          />
        </Tooltip>
      )}
      {type === "web" && (
        <Tooltip arrow title={"Movicare web" || "Verificar información"}>
          <Language style={{ color: "#2e4666" }} />
        </Tooltip>
      )}

      {type === "android" && (
        <Tooltip arrow title={"Movicare android" || "Verificar información"}>
          <Android style={{ color: "#35b32e" }} />
        </Tooltip>
      )}
      {type === "ios" && (
        <Tooltip arrow title={"Movicare ios" || "Verificar información"}>
          <Apple style={{ color: "#b4b4b4" }} />
        </Tooltip>
      )}
    </>
  );
};

/**
 * Componente que muestra la información relacionada con una franquicia y su origen.
 * Realiza consultas a Firestore para obtener los datos de la franquicia y el origen.
 * @param {Object} props
 * @param {string} props.franquicia El ID de la franquicia que se desea obtener de Firestore.
 * @param {Object | string} props.origen El origen del que se desea obtener la información. Puede ser un objeto de Firestore o un string.
 * @param {Object} props.sucursal La sucursal relacionada con la franquicia y el origen.
 * @param {Object} [props.rest] Otros props adicionales que pueden ser pasados al componente.
 * @returns {JSX.Element} Un componente que muestra la información de la franquicia y el origen, o un indicador de carga.
 */
const Origin = ({ franquicia, origen, sucursal, ...rest }) => {
  // console.log(" franquicia, origen, sucursal:", franquicia, origen, sucursal);
  /**
   * Estado que almacena la información del origen.
   * @type {Object|null}
   */
  const [infOrigen, setInfOrigen] = useState(null);
  /**
   * Estado que almacena la información de la franquicia.
   * @type {Object|undefined}
   */
  const [infoFranquicia, setInfofranquicia] = useState();
  /**
   * Estado que indica si los datos están siendo cargados.
   * @type {boolean}
   */
  const [loading, setLoading] = useState(true);
  /**
   * Función asincrónica que obtiene la información del origen desde Firestore.
   * Si el origen es un string, se establece directamente; si es un objeto de Firestore, se realiza una consulta.
   * @returns {Promise<void>} Una promesa que resuelve cuando la información del origen es obtenida.
   */
  const getOrigen = async () => {
    try {
      if (typeof origen === "string") {
        setInfOrigen(origen);
        setLoading(false);
      } else if (origen._path && origen._path.segments.length === 4) {
        const ref = doc(
          db,
          origen._path.segments[0], // Colección principal
          origen._path.segments[1], // ID del documento en la colección principal
          origen._path.segments[2], // Subcolección
          origen._path.segments[3] // ID del documento en la subcolección
        );
        const data = await getDoc(ref);
        // No se encuentro la referencia
        if (!data.exists()) {
          throw new Error("No fue posible obtener el origen desde _path.");
        }
        setInfOrigen({ ...data.data() });
        setLoading(false);
      } else {
        const getOrigen = await getDoc(origen);
        if (!getOrigen.exists) {
          throw new Error("No fue posible obtener el origen.");
        }
        const data = getOrigen.data();
        setInfOrigen({ ...data });
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error("Error obteniendo el origen:", error);
      setLoading(false);
    }
  };
  /**
   * Función asincrónica que obtiene la información de la franquicia desde Firestore.
   * @returns {Promise<void>} Una promesa que resuelve cuando la información de la franquicia es obtenida.
   */
  const getFranquicia = async () => {
    if (franquicia) {
      const reference = doc(db, "Franquicias", franquicia);
      const getReference = await getDoc(reference);
      if (!getReference.exists) {
        throw new Error("No fue posible obtener la franquicia");
      }
      const data = getReference.data();
      setInfofranquicia({ ...data });
      setLoading(false);
    }
    return;
  };
  /**
   * Efecto secundario que se ejecuta al montar el componente.
   * Llama a las funciones `getOrigen` y `getFranquicia` cuando el componente se monta.
   * @returns {void}
   */
  useEffect(() => {
    getOrigen();
    getFranquicia();
  }, []);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      {infoFranquicia && infOrigen && (
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Tooltip
            arrow
            title={infoFranquicia.Nombre || "Verificar información"}
          >
            <Avatar
              sx={{ width: 35, height: 35 }}
              src={infoFranquicia.avatar}
              alt={infoFranquicia.avatar}
            />
          </Tooltip>{" "}
          &nbsp;&nbsp;
          {`${infOrigen?.Nombre || infOrigen}` ?? "Verificar información"}
        </Grid>
      )}
      {!infoFranquicia && (
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <CustomToolTip type={infOrigen?.Nombre || infOrigen} /> &nbsp;&nbsp;
          {infOrigen?.Nombre || infOrigen}
        </Grid>
      )}
    </>
  );
};

export default Origin;
